import { invokeApiGateway } from '../../auth/api-client';
import type { Environment } from '../types/debugger';

export interface S3Execution {
  id: string;
  lastModified: string;
  nodeCount: number;
  duration?: number;
  status?: string;
}

export interface S3FileEntry {
  key: string;
  name: string;
  size: number;
  type: 'file' | 'directory';
  lastModified: string;
}

export interface ExecutionSummaryData {
  execution_id: string;
  workflow_name: string;
  prefix: string;
  started_at: string;
  ended_at: string;
  total_duration_ms: number;
  node_count: number;
  nodes: Array<{
    node_id: string;
    status: string;
    duration_ms: number;
    keys_added: string[];
    fields_mutated: Record<string, string[]>;
  }>;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export async function listBuckets(env: string) {
  return invokeApiGateway(`/debug/s3/buckets?env=${env.toLowerCase()}`);
}

export async function listObjects(env: string, bucket: string, prefix: string, maxKeys = 100) {
  return invokeApiGateway(`/debug/s3/list?env=${env.toLowerCase()}`, 'POST', { bucket, prefix, max_keys: maxKeys });
}

export async function getObject(env: string, bucket: string, key: string) {
  return invokeApiGateway(`/debug/s3/object?env=${env.toLowerCase()}`, 'POST', { bucket, key });
}

const LOGS_BUCKET_PREFIX = 'aag-agents-agent-logs-';
const TRACES_PREFIX = 'graph_traces/campaign-strategist/';

function logsBucket(env: string): string {
  return `${LOGS_BUCKET_PREFIX}${env.toLowerCase()}-na`;
}

export async function listExecutions(env: Environment): Promise<S3Execution[]> {
  const data = await listObjects(env, logsBucket(env), TRACES_PREFIX, 500);
  const execMap = new Map<string, S3Execution>();
  for (const obj of data.objects || []) {
    const relative = (obj.key as string).replace(TRACES_PREFIX, '');
    const parts = relative.split('/');
    const id = parts[0];
    if (!id) continue;
    if (!execMap.has(id)) {
      execMap.set(id, { id, lastModified: obj.lastModified || '', nodeCount: 0 });
    }
    // Count all .json files (root + subdir), excluding _summary and final
    const fileName = parts[parts.length - 1];
    if (fileName.endsWith('.json') && fileName !== '_summary.json' && fileName !== 'final.json') {
      execMap.get(id)!.nodeCount++;
    }
  }
  return Array.from(execMap.values());
}

export async function listFiles(env: Environment, executionId: string, prefix?: string): Promise<S3FileEntry[]> {
  const fullPrefix = prefix
    ? `${TRACES_PREFIX}${executionId}/${prefix}`
    : `${TRACES_PREFIX}${executionId}/`;
  const data = await listObjects(env, logsBucket(env), fullPrefix);
  return (data.objects || []).map((o: any) => ({
    key: o.key.replace(`${TRACES_PREFIX}${executionId}/`, ''),
    name: o.key.split('/').pop() || o.key,
    size: o.size || 0,
    type: o.key.endsWith('/') ? 'directory' as const : 'file' as const,
    lastModified: o.lastModified || '',
  }));
}

export async function getFileContent(env: Environment, executionId: string, fileKey: string): Promise<unknown> {
  const fullKey = `${TRACES_PREFIX}${executionId}/${fileKey}`;
  return getObject(env, logsBucket(env), fullKey);
}

export async function getExecutionSummary(env: Environment, executionId: string): Promise<ExecutionSummaryData> {
  return getFileContent(env, executionId, '_summary.json') as Promise<ExecutionSummaryData>;
}

export async function searchExecution(env: Environment, executionId: string): Promise<S3FileEntry[]> {
  return listFiles(env, executionId);
}
